/**
 * Settings Context
 *
 * 앱 설정 관리 (글자 크기, 언어)
 *
 * 언어 우선순위:
 * 1차: 사용자 직접 설정 (설정 화면에서 변경 → localStorage user_language_override=true)
 * 2차: 프로젝트 기본 언어 (project default_language)
 * 3차: 대상자 선호 언어 (participant preferred_language)
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { LAST_SESSION_KEY } from '@/constants/storage';

export type FontSize = 'default' | 'large';
export type Language = 'ko' | 'en';

interface Settings {
  fontSize: FontSize;
  language: Language;
}

interface SettingsContextValue {
  settings: Settings;
  setFontSize: (size: FontSize) => void;
  setLanguage: (lang: Language, scopeKey?: string) => void;
  hasUserLanguageOverride: (scopeKey?: string) => boolean;
  /** 서버/대상자 언어 정보로 언어 우선순위 적용 */
  applyLanguagePriority: (config: {
    serverLanguage?: string | null;
    participantPreferredLanguage?: string | null;
  }, scopeKey?: string) => void;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

const SETTINGS_STORAGE_KEY = 'patient-app-settings';
const USER_LANGUAGE_OVERRIDE_KEY = 'patient-language-override';

interface LanguageOverrideState {
  scopeKey: string;
  language: Language;
}

const defaultSettings: Settings = {
  fontSize: 'default',
  language: 'ko',
};

function isValidLanguage(lang: string | null | undefined): lang is Language {
  return lang === 'ko' || lang === 'en';
}

function getActiveScopeKey(scopeKey?: string): string | null {
  if (scopeKey) return scopeKey;
  return sessionStorage.getItem(LAST_SESSION_KEY);
}

function readLanguageOverrideState(): LanguageOverrideState | null {
  try {
    const stored = localStorage.getItem(USER_LANGUAGE_OVERRIDE_KEY);
    if (!stored) return null;

    // Legacy boolean flag from older builds should not block scoped language priority.
    if (stored === 'true' || stored === 'false') return null;

    const parsed = JSON.parse(stored) as Partial<LanguageOverrideState>;
    if (!parsed || typeof parsed.scopeKey !== 'string' || !isValidLanguage(parsed.language)) {
      return null;
    }
    return { scopeKey: parsed.scopeKey, language: parsed.language };
  } catch {
    return null;
  }
}

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<Settings>(() => {
    try {
      const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : defaultSettings;
    } catch {
      return defaultSettings;
    }
  });

  useEffect(() => {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));

    // Apply font size to document
    document.documentElement.setAttribute('data-font-size', settings.fontSize);

    // Apply language to document
    document.documentElement.setAttribute('lang', settings.language);
  }, [settings]);

  const setFontSize = useCallback((size: FontSize) => {
    setSettings((prev) => ({ ...prev, fontSize: size }));
  }, []);

  const setLanguage = useCallback((lang: Language, scopeKey?: string) => {
    const activeScopeKey = getActiveScopeKey(scopeKey);
    if (activeScopeKey) {
      localStorage.setItem(
        USER_LANGUAGE_OVERRIDE_KEY,
        JSON.stringify({ scopeKey: activeScopeKey, language: lang } satisfies LanguageOverrideState),
      );
    }
    setSettings((prev) => ({ ...prev, language: lang }));
  }, []);

  const hasUserLanguageOverride = useCallback(
    (scopeKey?: string) => {
      const activeScopeKey = getActiveScopeKey(scopeKey);
      const overrideState = readLanguageOverrideState();
      return !!activeScopeKey && !!overrideState && overrideState.scopeKey === activeScopeKey;
    },
    [],
  );

  const applyLanguagePriority = useCallback((config: {
    serverLanguage?: string | null;
    participantPreferredLanguage?: string | null;
  }, scopeKey?: string) => {
    // 1순위: 사용자가 직접 설정한 경우 서버/대상자 언어로 덮어쓰지 않음
    if (hasUserLanguageOverride(scopeKey)) return;

    // 우선순위: 2차 프로젝트 기본 언어 → 3차 대상자 선호 언어
    const { serverLanguage, participantPreferredLanguage } = config;

    let resolvedLanguage: Language | null = null;

    if (isValidLanguage(serverLanguage)) {
      resolvedLanguage = serverLanguage;
    } else if (isValidLanguage(participantPreferredLanguage)) {
      resolvedLanguage = participantPreferredLanguage;
    }

    if (resolvedLanguage) {
      setSettings((prev) => ({ ...prev, language: resolvedLanguage }));
    }
  }, [hasUserLanguageOverride]);

  const value = useMemo(
    () => ({ settings, setFontSize, setLanguage, hasUserLanguageOverride, applyLanguagePriority }),
    [settings, setFontSize, setLanguage, hasUserLanguageOverride, applyLanguagePriority],
  );

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};

/** 언어 설정만 필요한 컴포넌트용 */
// eslint-disable-next-line react-refresh/only-export-components
export const useLanguage = (): Language => {
  const { settings } = useSettings();
  return settings.language;
};

/** 폰트 크기 설정만 필요한 컴포넌트용 */
// eslint-disable-next-line react-refresh/only-export-components
export const useFontSize = (): FontSize => {
  const { settings } = useSettings();
  return settings.fontSize;
};
